import { atom } from 'jotai';

import { convertUnit, Unit } from '@core';
import { CorridorParameterMargins } from '@data';

import { parametersByGraphLineAtom, swingMeasuredParametersMapAtom } from '../global/uiNodes';

import { corridorsAtom } from './corridors';
import { layoutCustomCorridorsMapAtom } from './layout';
import { activeUserSettingsAtom } from './user';

export type GraphCorridorDataPoint = CorridorParameterMargins & {
    name: string;
    position: string | null;
};

const graphLineCorridorsBySourceAtom = atom((get) => {
    const parametersByGraphLine = get(parametersByGraphLineAtom);
    const corridors = get(corridorsAtom) ?? {};
    const goalCorridors = get(layoutCustomCorridorsMapAtom) ?? {};
    const swingMeasuredParametersMap = get(swingMeasuredParametersMapAtom);
    const activeUserSettings = get(activeUserSettingsAtom);

    return Object.fromEntries(
        Object.entries(parametersByGraphLine).map(([graphLineId, parameters]) => {
            const goalCorridorsForLine = parameters.flatMap(({ parameter }) => {
                const goalCorridor = goalCorridors[parameter.smpId];
                const measurement = swingMeasuredParametersMap[parameter.smpId];
                if (!goalCorridor || !activeUserSettings || !measurement) return [];
                const innerMax = convertUnit({
                    unit: measurement.units?.notation as Unit,
                    value: goalCorridor.innerMax,
                    userPreferredUnit: activeUserSettings.userPreferredUnit,
                });
                const innerMin = convertUnit({
                    unit: measurement.units?.notation as Unit,
                    value: goalCorridor.innerMin,
                    userPreferredUnit: activeUserSettings.userPreferredUnit,
                });
                return [
                    {
                        innerMin: innerMin.value,
                        innerMax: innerMax.value,
                        unit: innerMin.unit,
                        name: parameter.name,
                        position: parameter.position,
                    },
                ];
            });
            const systemCorridorsForLine = parameters.flatMap(({ parameter }) => {
                const systemCorridor = corridors[parameter.name];
                if (!systemCorridor) return [];
                return [{ ...systemCorridor, name: parameter.name, position: parameter.position }];
            });
            return [graphLineId, { goalCorridors: goalCorridorsForLine, systemCorridors: systemCorridorsForLine }];
        }),
    );
});

export const graphLineCorridorsAtom = atom((get) => {
    const userSettings = get(activeUserSettingsAtom);
    const corridorsBySource = get(graphLineCorridorsBySourceAtom);

    if (userSettings.showGoal)
        return Object.fromEntries(
            Object.entries(corridorsBySource).map(([graphLineId, corridors]) => [graphLineId, corridors.goalCorridors]),
        );
    if (userSettings.showCorridors)
        return Object.fromEntries(
            Object.entries(corridorsBySource).map(([graphLineId, corridors]) => [
                graphLineId,
                corridors.systemCorridors,
            ]),
        );
    return {};
});
